import { useState, useContext, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Users, MapPin, CalendarDays, Check, Phone, ArrowLeft, Loader2, Share2, ClipboardCheck, ShieldCheck } from 'lucide-react';
import axios from '../lib/apiTransport';
import { toast } from 'sonner';
import { API } from '../App';
import { normalizeApiError } from '../utils/normalizeError';
import Footer from '../components/Footer';
import DateOfBirthPicker from '../components/DateOfBirthPicker';

const AUDIENCE_PREVIEWS = [
  { title: 'Players', detail: 'Find public sessions and reserve a spot', icon: MapPin, color: 'bg-emerald-500' },
  { title: 'Trainers & Programs', detail: 'Publish schedules and organize participants', icon: CalendarDays, color: 'bg-amber-500' },
  { title: 'Hosts', detail: 'Share one link and run game day', icon: ClipboardCheck, color: 'bg-orange-500' },
];

const WHY_POINTS = [
  'One shareable link',
  'Live rosters and waitlists',
  'Invitations and player communication',
  'Game-day management',
];

const AUDIENCE_PATHS = [
  {
    id: 'players',
    eyebrow: 'For Players',
    title: 'Find your next place to play',
    description: 'Browse public basketball sessions, see the facts, reserve your spot, and receive updates from the host.',
    icon: Users,
    items: ['Find basketball near you', 'Join public games and training', 'See capacity and open spots', 'Stay updated when details change'],
    cta: 'Find a session',
  },
  {
    id: 'trainers',
    eyebrow: 'For Trainers & Programs',
    title: 'Your public home for training sessions',
    description: 'Publish a trainer or program page, organize recurring sessions, and give participants one consistent place for session details.',
    icon: CalendarDays,
    items: ['Publish a public trainer or program page', 'Create recurring session schedules', 'Manage invitations, rosters, and waitlists', 'Share updates and check in players'],
    cta: 'Explore trainer tools',
    href: '/trainers',
  },
  {
    id: 'hosts',
    eyebrow: 'For Hosts',
    title: 'Make your run easier to organize',
    description: 'Create a game, share its canonical link, manage participation, and keep game-day operations in one place.',
    icon: Share2,
    items: ['Create and duplicate games', 'Share one canonical link', 'Manage rosters and waitlists', 'Message, check in, update, and cancel'],
    cta: 'Start hosting',
  },
];

const Landing = () => {
  // Mobile detection for auth shell selection (Dialog on desktop, bottom Sheet on
  // mobile). Initialized synchronously to avoid a desktop→mobile flash on first
  // render. The bottom-sheet anchor eliminates iOS keyboard jitter caused by
  // dvh recalculation in the centered Dialog.
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(max-width: 640px)').matches;
  });
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 640px)');
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [showAuth, setShowAuth] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  
  // Phone login state
  const [loginMode, setLoginMode] = useState('phone'); // 'phone' | 'email'
  const [phoneLoginNumber, setPhoneLoginNumber] = useState('');
  const [phoneLoginCode, setPhoneLoginCode] = useState('');
  const [phoneLoginStep, setPhoneLoginStep] = useState('input'); // 'input' | 'verify'
  const [phoneLoginSending, setPhoneLoginSending] = useState(false);
  const [phoneLoginVerifying, setPhoneLoginVerifying] = useState(false);
  const [phoneLoginError, setPhoneLoginError] = useState('');
  const [phoneLoginCanResend, setPhoneLoginCanResend] = useState(false);
  const [phoneLoginResendTimer, setPhoneLoginResendTimer] = useState(0);
  
  // Phone verification state
  const [phoneVerificationEnabled, setPhoneVerificationEnabled] = useState(false);
  const [signupStep, setSignupStep] = useState(1); // 1: form, 2: phone verify
  const [verificationCode, setVerificationCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [canResend, setCanResend] = useState(true);
  const [resendTimer, setResendTimer] = useState(0);
  
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    date_of_birth: '',
    phone_number: '',
    sms_opt_in: false,
  });
  
  // Check if we were redirected from a run preview or a club migration link.
  // location.state covers React-Router soft navigations; ?openAuth=1 covers
  // hard navigations (e.g., ClubDetail -> setShowAuth(true) at app root).
  useEffect(() => {
    let openAuthFromQuery = false;
    try {
      openAuthFromQuery = new URLSearchParams(window.location.search).get('openAuth') === '1';
    } catch (_) { /* noop */ }
    if (location.state?.showAuth || openAuthFromQuery) {
      setShowAuth(true);
    }
  }, [location]);
  
  const [dobValidation, setDobValidation] = useState({ isValid: false, error: '' });

  // Check if phone verification is enabled on mount
  useEffect(() => {
    const checkPhoneVerification = async () => {
      try {
        const response = await axios.get(`${API}/auth/phone-verification-status`);
        setPhoneVerificationEnabled(response.data.enabled);
      } catch (err) {
        // If endpoint fails, assume disabled
        setPhoneVerificationEnabled(false);
      }
    };
    checkPhoneVerification();
  }, []);

  // Resend timer
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [resendTimer]);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    
    // Format as (XXX) XXX-XXXX
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setFormData({ ...formData, phone_number: formatted });
  };

  const normalizePhone = (phone) => {
    const digits = phone.replace(/\D/g, '');
    return digits.length === 10 ? `+1${digits}` : `+${digits}`;
  };

  const sendVerificationCode = async () => {
    if (!formData.phone_number) {
      toast.error('Please enter your phone number');
      return;
    }

    setIsSendingCode(true);
    try {
      const response = await axios.post(`${API}/auth/send-otp`, {
        phone_number: normalizePhone(formData.phone_number)
      });
      toast.success('Verification code sent!');
      setSignupStep(2);
      setCanResend(false);
      setResendTimer(response.data.resend_cooldown || 30);
    } catch (error) {
      // Handle cooldown errors with remaining time
      const errorData = error.response?.data;
      if (errorData?.detail?.cooldown_remaining) {
        setCanResend(false);
        setResendTimer(errorData.detail.cooldown_remaining);
        toast.error(`Please wait ${errorData.detail.cooldown_remaining} seconds before resending`);
      } else {
        toast.error(normalizeApiError(error, 'Failed to send code'));
      }
    } finally {
      setIsSendingCode(false);
    }
  };

  const verifyCode = async () => {
    if (verificationCode.length !== 6) {
      toast.error('Please enter the 6-digit code');
      return;
    }

    setIsVerifying(true);
    try {
      await axios.post(`${API}/auth/verify-otp`, {
        phone_number: normalizePhone(formData.phone_number),
        code: verificationCode
      });
      
      // Phone verified - now complete registration
      await completeRegistration(true);
    } catch (error) {
      toast.error(normalizeApiError(error, 'Invalid code'));
    } finally {
      setIsVerifying(false);
    }
  };

  const handlePostAuthRedirect = () => {
    // Check for pending run from deferred deep linking (Phase 2A: include club context)
    const pendingRunId = localStorage.getItem('pendingRunId');
    const pendingClubId = localStorage.getItem('pendingClubId');
    
    if (pendingRunId) {
      localStorage.removeItem('pendingRunId');
      localStorage.removeItem('pendingClubId');
      
      const redirectUrl = pendingClubId 
        ? `/run/${pendingRunId}?club=${pendingClubId}` 
        : `/run/${pendingRunId}`;
      // Hard navigation: bypasses React Router so the `/` route guard's
      // <Navigate to="/dashboard"> can't fire after setToken and clobber
      // the deep-link destination. Tokens are already in localStorage,
      // so bootstrapAuth picks them up on reload — user lands at the run
      // page fully authenticated.
      window.location.replace(redirectUrl);
      return true;
    }
    
    // Club link flow: an anonymous user opened /club/<slug>, tapped Join Club,
    // and we stashed the slug in localStorage. After auth, send them back to
    // the club page only. Joining still requires an explicit second click.
    const pendingClubSlug = localStorage.getItem('pendingClubSlug');
    if (pendingClubSlug) {
      localStorage.removeItem('pendingClubSlug');
      window.location.replace(`/club/${pendingClubSlug}`);
      return true;
    }
    
    if (location.state?.returnTo) {
      window.location.replace(location.state.returnTo);
      return true;
    }
    
    return false;
  };

  const completeRegistration = async (phoneVerified = false) => {
    try {
      const payload = {
        username: formData.username,
        email: formData.email,
        password: formData.password,
        date_of_birth: formData.date_of_birth,
        phone_number: formData.phone_number ? normalizePhone(formData.phone_number) : null,
        phone_verified: phoneVerified,
        sms_opt_in: formData.sms_opt_in
      };
      
      const response = await axios.post(`${API}/auth/register`, payload);
      login(response.data.access_token, response.data.refresh_token, response.data.user);
      toast.success('Account created successfully!');
      resetForm();
      
      // Handle deferred deep linking
      handlePostAuthRedirect();
    } catch (error) {
      toast.error(normalizeApiError(error));
    }
  };

  // Phone login resend timer
  useEffect(() => {
    if (phoneLoginResendTimer > 0) {
      const timer = setTimeout(() => setPhoneLoginResendTimer(t => t - 1), 1000);
      return () => clearTimeout(timer);
    }
    if (phoneLoginResendTimer === 0 && phoneLoginStep === 'verify') {
      setPhoneLoginCanResend(true);
    }
  }, [phoneLoginResendTimer, phoneLoginStep]);

  const handlePhoneSendCode = async () => {
    if (!phoneLoginNumber.trim()) return;
    setPhoneLoginSending(true);
    setPhoneLoginError('');
    try {
      await axios.post(`${API}/auth/phone/send-code`, { phone_number: phoneLoginNumber.trim() });
      setPhoneLoginStep('verify');
      setPhoneLoginCanResend(false);
      setPhoneLoginResendTimer(30);
    } catch {
      setPhoneLoginError('Unable to send code. Please try again.');
    } finally {
      setPhoneLoginSending(false);
    }
  };

  const handlePhoneVerifyCode = async () => {
    if (phoneLoginCode.length !== 6) return;
    setPhoneLoginVerifying(true);
    setPhoneLoginError('');
    // Blur active input BEFORE the network call so iOS keyboard dismissal
    // happens during the round-trip, not during the post-login route swap.
    // Prevents WKWebView viewport-reflow jitter on TestFlight.
    try { document.activeElement?.blur?.(); } catch (_) { /* noop */ }
    try {
      const resp = await axios.post(`${API}/auth/phone/verify-code`, {
        phone_number: phoneLoginNumber.trim(),
        code: phoneLoginCode.trim(),
      });
      const { access_token, refresh_token, user } = resp.data;
      login(access_token, refresh_token, user);

      // Deep-link destinations use window.location.replace() in
      // handlePostAuthRedirect (returns true). Otherwise go to /dashboard.
      const redirected = handlePostAuthRedirect();
      if (!redirected) {
        navigate('/dashboard', { replace: true });
      }
    } catch (err) {
      setPhoneLoginError(err.response?.data?.detail || 'Verification failed. Please try again.');
    } finally {
      setPhoneLoginVerifying(false);
    }
  };

  const resetPhoneLogin = () => {
    setPhoneLoginStep('input');
    setPhoneLoginCode('');
    setPhoneLoginError('');
    setPhoneLoginCanResend(false);
    setPhoneLoginResendTimer(0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isLogin) {
      // Login flow
      // Blur active input BEFORE the network call so iOS keyboard dismissal
      // happens during the round-trip, not during the post-login route swap.
      // Prevents WKWebView viewport-reflow jitter on TestFlight.
      try { document.activeElement?.blur?.(); } catch (_) { /* noop */ }
      try {
        const response = await axios.post(`${API}/auth/login`, {
          identifier: formData.username.trim().toLowerCase(),
          password: formData.password
        });
        login(response.data.access_token, response.data.refresh_token, response.data.user);

        // handlePostAuthRedirect uses window.location.replace() for deep-link
        // destinations (returns true and triggers a hard reload). Tokens are
        // already in localStorage so bootstrapAuth re-auths after reload.
        // If no deep link, we navigate to /dashboard via React Router.
        const redirected = handlePostAuthRedirect();
        if (!redirected) {
          navigate('/dashboard', { replace: true });
          toast.success('Welcome back!');
        }
        // Note: when redirected=true, no toast — the reload happens too fast
        // for the toast to anchor anywhere meaningful.
      } catch (error) {
        toast.error(normalizeApiError(error));
      }
    } else {
      // Signup flow
      
      // STRICT AGE VALIDATION - must be at least 13 years old
      if (!dobValidation.isValid) {
        if (dobValidation.error) {
          toast.error(dobValidation.error);
        } else {
          toast.error('Please enter a valid date of birth');
        }
        return;
      }
      
      // Validate phone is provided when verification is enabled
      if (phoneVerificationEnabled && !formData.phone_number) {
        toast.error('Phone number is required');
        return;
      }
      
      if (phoneVerificationEnabled && formData.phone_number) {
        // Go to phone verification step
        await sendVerificationCode();
      } else {
        // Complete registration without phone verification
        await completeRegistration(false);
      }
    }
  };

  const resetForm = () => {
    setShowAuth(false);
    setSignupStep(1);
    setVerificationCode('');
    setFormData({
      username: '',
      email: '',
      password: '',
      date_of_birth: '',
      phone_number: '',
      sms_opt_in: false,
    });
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      toast.success('If the email exists, a reset link has been sent');
      setShowForgotPassword(false);
      setForgotEmail('');
    } catch (error) {
      toast.error(normalizeApiError(error));
    }
  };

  return (
    <div className="min-h-screen bg-[#FDF8F3]">
      {/* HEADER NAV */}
      <header
        className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-zinc-100"
        style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))' }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between pb-4">
            {/* Logo - using width for horizontal logo */}
            <img 
              src="/logo-transparent.png" 
              alt="Open Runs USA" 
              className="h-auto w-[112px] shrink-0 sm:w-[200px]"
              data-testid="header-logo"
            />

            <nav className="hidden items-center gap-6 text-sm font-semibold text-zinc-700 md:flex" aria-label="Audience navigation">
              <a href="#players" className="transition-colors hover:text-[#FF6A00]">Players</a>
              <Link to="/trainers" className="transition-colors hover:text-[#FF6A00]">Trainers</Link>
              <a href="#hosts" className="transition-colors hover:text-[#FF6A00]">Hosts</a>
            </nav>
            
            {/* Nav Buttons */}
            <div className="flex items-center gap-1.5 sm:gap-3">
              <Button
                data-testid="login-btn"
                variant="outline"
                onClick={() => { setIsLogin(true); setShowAuth(true); }}
                className="rounded-lg border-2 border-[#FF6A00] bg-transparent px-3 py-2 text-xs font-semibold text-[#FF6A00] transition-all duration-200 hover:bg-[#FF6A00]/5 sm:px-6 sm:py-2.5 sm:text-base"
              >
                Login
              </Button>
              <Button
                data-testid="get-started-btn"
                onClick={() => { setIsLogin(false); setShowAuth(true); }}
                className="rounded-lg bg-[#FF6A00] px-3 py-2 text-xs font-semibold text-white transition-all duration-200 hover:bg-[#FF8A33] sm:px-6 sm:py-2.5 sm:text-base"
              >
                Get Started
              </Button>
            </div>
          </div>
          <nav className="flex items-center justify-center gap-6 border-t border-zinc-100 pt-3 text-sm font-semibold text-zinc-700 md:hidden" aria-label="Mobile audience navigation">
            <a href="#players" className="transition-colors hover:text-[#FF6A00]">Players</a>
            <Link to="/trainers" className="transition-colors hover:text-[#FF6A00]">Trainers</Link>
            <a href="#hosts" className="transition-colors hover:text-[#FF6A00]">Hosts</a>
          </nav>
        </div>
      </header>

      {/* HERO SECTION */}
      <section className="relative pt-10 sm:pt-16 pb-12 sm:pb-16 px-4 sm:px-6 lg:px-8 overflow-hidden">
        {/* Subtle radial gradient spotlight */}
        <div 
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse 80% 50% at 50% 30%, rgba(255, 106, 0, 0.08) 0%, transparent 60%)',
          }}
        />
        
        <div className="relative max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-12 items-center">
            {/* LEFT: Hero Content */}
            <div className="text-center lg:text-left">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 bg-white border border-zinc-200 rounded-full px-4 py-1.5 mb-6 shadow-sm">
                <span className="relative inline-flex h-2 w-2 rounded-full bg-[#FF6A00]"></span>
                <span className="text-sm font-medium text-zinc-600">A platform for organizing and joining basketball sessions</span>
              </div>

              {/* Headline */}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0B0B0B] leading-[1.1] tracking-tight mb-5">
                Stop Running Your Games{' '}
                <span className="text-[#FF6A00]">Out of a Group Chat</span>
              </h1>

              {/* Subheadline */}
              <p className="text-base sm:text-lg text-zinc-600 leading-relaxed mb-8 max-w-lg mx-auto lg:mx-0">
                A group chat can announce a game. OpenRuns keeps the session organized with one place for the shareable link, roster, invitations, updates, and game-day details.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
                <Button
                  onClick={() => { setIsLogin(false); setShowAuth(true); }}
                  className="bg-[#FF6A00] hover:bg-[#FF8A33] text-white h-12 sm:h-14 px-8 text-base sm:text-lg font-semibold rounded-xl shadow-lg shadow-orange-500/20 transition-all duration-200"
                  data-testid="hero-cta"
                >
                  Get Started
                </Button>
              </div>

              {/* Why points - horizontal on mobile, visible under CTA */}
              <div className="flex flex-wrap justify-center lg:justify-start gap-x-4 gap-y-2 mt-6 text-sm text-zinc-500">
                {WHY_POINTS.map((point) => (
                  <span key={`why-${point}`} className="flex items-center gap-1.5">
                    <Check className="w-4 h-4 text-emerald-500" />
                    {point}
                  </span>
                ))}
              </div>
            </div>

            {/* RIGHT: Game Preview Cards */}
            <div className="relative lg:pl-8">
              {/* Vertical stack of preview cards */}
              <div className="space-y-3 max-w-md mx-auto lg:mx-0">
                {AUDIENCE_PREVIEWS.map((audience) => (
                  <div 
                    key={audience.title}
                    className="bg-white rounded-2xl border border-zinc-200/80 p-4 shadow-sm hover:shadow-md transition-shadow duration-200"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 ${audience.color} rounded-full flex items-center justify-center`}>
                        <audience.icon className="w-5 h-5 text-white" aria-hidden="true" />
                      </div>
                      <div>
                        <p className="font-semibold text-[#0B0B0B]">{audience.title}</p>
                        <p className="text-sm text-zinc-500">{audience.detail}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Decorative floating elements */}
              <div className="hidden lg:block absolute -top-4 -right-4 w-20 h-20 bg-orange-100 rounded-full blur-2xl opacity-60" />
              <div className="hidden lg:block absolute -bottom-4 -left-4 w-16 h-16 bg-emerald-100 rounded-full blur-xl opacity-60" />
            </div>
          </div>
        </div>
      </section>

      {/* THREE AUDIENCE PATHS */}
      <section className="bg-white px-4 py-16 sm:px-6 sm:py-20 lg:px-8" aria-labelledby="audience-heading">
        <div className="mx-auto max-w-6xl">
          <div className="mx-auto mb-12 max-w-2xl text-center">
            <p className="mb-2 text-sm font-bold uppercase tracking-[0.2em] text-[#FF6A00]">One platform, three ways to use it</p>
            <h2 id="audience-heading" className="text-3xl font-bold text-[#0B0B0B] sm:text-4xl">
              Built around the basketball session
            </h2>
            <p className="mt-4 text-zinc-600">
              Players join. Hosts operate. Trainers publish. Everyone works from the same session details.
            </p>
          </div>

          <div className="grid gap-6 lg:grid-cols-3">
            {AUDIENCE_PATHS.map((audience) => (
              <article
                key={audience.id}
                id={audience.id}
                className="scroll-mt-32 rounded-3xl border border-zinc-200 bg-[#FDF8F3] p-6 shadow-sm"
                data-testid={`audience-${audience.id}`}
              >
                <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-orange-100">
                  <audience.icon className="h-6 w-6 text-[#FF6A00]" aria-hidden="true" />
                </div>
                <p className="text-sm font-bold uppercase tracking-wide text-[#FF6A00]">{audience.eyebrow}</p>
                <h3 className="mt-2 text-2xl font-bold text-[#0B0B0B]">{audience.title}</h3>
                <p className="mt-3 text-sm leading-6 text-zinc-600">{audience.description}</p>
                <ul className="mt-5 space-y-3 text-sm text-zinc-700">
                  {audience.items.map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                {audience.href ? (
                  <Button asChild variant="outline" className="mt-6 w-full rounded-xl border-orange-200 text-[#C44F00] hover:bg-orange-50">
                    <Link to={audience.href}>{audience.cta}</Link>
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    onClick={() => { setIsLogin(false); setShowAuth(true); }}
                    className="mt-6 w-full rounded-xl border-orange-200 text-[#C44F00] hover:bg-orange-50"
                  >
                    {audience.cta}
                  </Button>
                )}
              </article>
            ))}
          </div>

          <aside className="mx-auto mt-10 flex max-w-3xl items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-5 text-left">
            <ShieldCheck className="mt-0.5 h-6 w-6 shrink-0 text-emerald-700" aria-hidden="true" />
            <div>
              <h3 className="font-semibold text-emerald-950">Youth privacy stays in force</h3>
              <p className="mt-1 text-sm leading-6 text-emerald-900">
                Public trainer and session pages do not display minor contact information, and youth-sensitive locations use the platform&apos;s public-location redaction.
              </p>
            </div>
          </aside>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8 bg-[#0B0B0B]">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
            Ready to organize your next basketball session?
          </h2>
          <p className="text-zinc-400 mb-8">
            Join a session, host a game, or publish your training schedule.
          </p>
          <Button
            onClick={() => { setIsLogin(false); setShowAuth(true); }}
            className="bg-[#FF6A00] hover:bg-[#FF8A33] text-white h-12 sm:h-14 px-10 text-base sm:text-lg font-semibold rounded-xl"
          >
            Get Started Free
          </Button>
        </div>
      </section>

      <Footer />

      {/* AUTH DIALOG — bottom Sheet on mobile, centered Dialog on desktop.
          Mobile bottom-sheet eliminates iOS keyboard jitter caused by dvh
          recalc in the centered Dialog. Body JSX is declared once and reused
          in both shells. */}
      {(() => {
        const handleOpenChange = (open) => {
          if (!open) { resetForm(); resetPhoneLogin(); setLoginMode('phone'); }
          setShowAuth(open);
        };
        const authBody = (
          <>
            <DialogHeader className="flex-shrink-0">
            <DialogTitle className="font-heading text-2xl font-bold text-center text-[#0B0B0B]">
              {isLogin
                ? 'WELCOME BACK'
                : (signupStep === 1 ? 'Create Account' : 'Verify Phone')}
            </DialogTitle>
          </DialogHeader>
          
          {/* ═══ PHONE LOGIN (primary when isLogin + loginMode=phone) ═══ */}
          {isLogin && loginMode === 'phone' && (
            <div className="space-y-4 mt-4 pb-4">
              {phoneLoginStep === 'input' ? (
                <>
                  <div>
                    <Label htmlFor="phone-login" className="text-[#0B0B0B] font-medium">Phone Number</Label>
                    <Input
                      id="phone-login"
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      name="tel"
                      data-testid="phone-login-input"
                      value={phoneLoginNumber}
                      onChange={e => setPhoneLoginNumber(e.target.value)}
                      placeholder="(555) 123-4567"
                      className="mt-1.5 h-12 border-[#E5E7EB] focus:border-[#FF6A00] focus:ring-[#FF6A00]/20 text-lg"
                      onKeyDown={e => e.key === 'Enter' && handlePhoneSendCode()}
                    />
                  </div>
                  {phoneLoginError && (
                    <p className="text-sm text-red-600">{phoneLoginError}</p>
                  )}
                  <Button
                    type="button"
                    onClick={handlePhoneSendCode}
                    disabled={phoneLoginSending || !phoneLoginNumber.trim()}
                    className="w-full h-12 bg-[#FF6A00] hover:bg-[#e55f00] text-white font-semibold rounded-xl transition-all active:scale-[0.98]"
                    data-testid="phone-send-code-btn"
                  >
                    {phoneLoginSending ? 'Sending...' : 'Send Code'}
                  </Button>
                </>
              ) : (
                <>
                  <p className="text-sm text-zinc-500 text-center">
                    Enter the 6-digit code sent to <span className="font-medium text-zinc-800">{phoneLoginNumber}</span>
                  </p>
                  <div>
                    <Input
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      data-testid="phone-verify-code-input"
                      value={phoneLoginCode}
                      onChange={e => {
                        const v = e.target.value.replace(/\D/g, '').slice(0, 6);
                        setPhoneLoginCode(v);
                      }}
                      placeholder="000000"
                      className="mt-1 h-14 text-center text-2xl tracking-[0.3em] font-mono border-[#E5E7EB] focus:border-[#FF6A00] focus:ring-[#FF6A00]/20"
                      maxLength={6}
                      autoFocus
                      onKeyDown={e => e.key === 'Enter' && phoneLoginCode.length === 6 && handlePhoneVerifyCode()}
                    />
                  </div>
                  {phoneLoginError && (
                    <p className="text-sm text-red-600 text-center">{phoneLoginError}</p>
                  )}
                  <Button
                    type="button"
                    onClick={handlePhoneVerifyCode}
                    disabled={phoneLoginVerifying || phoneLoginCode.length !== 6}
                    className="w-full h-12 bg-[#FF6A00] hover:bg-[#e55f00] text-white font-semibold rounded-xl transition-all active:scale-[0.98]"
                    data-testid="phone-verify-btn"
                  >
                    {phoneLoginVerifying ? 'Verifying...' : 'Verify & Log In'}
                  </Button>
                  <div className="flex items-center justify-between text-sm">
                    <button
                      type="button"
                      onClick={resetPhoneLogin}
                      className="text-zinc-500 hover:text-zinc-700 transition-colors"
                      data-testid="phone-edit-number"
                    >
                      Edit phone number
                    </button>
                    {phoneLoginCanResend ? (
                      <button
                        type="button"
                        onClick={() => { resetPhoneLogin(); handlePhoneSendCode(); }}
                        className="text-[#FF6A00] hover:text-[#e55f00] font-medium transition-colors"
                        data-testid="phone-resend-code"
                      >
                        Resend code
                      </button>
                    ) : phoneLoginResendTimer > 0 ? (
                      <span className="text-zinc-400">Resend in {phoneLoginResendTimer}s</span>
                    ) : null}
                  </div>
                  <p className="text-xs text-zinc-400 text-center mt-1">
                    Didn't get a code? You can resend or use email login.
                  </p>
                </>
              )}
              <div className="pt-2 border-t border-zinc-200">
                <button
                  type="button"
                  onClick={() => setLoginMode('email')}
                  className="w-full text-center text-sm text-zinc-500 hover:text-zinc-700 py-2 transition-colors"
                  data-testid="switch-to-email-login"
                >
                  Use email or username instead
                </button>
              </div>
              <p className="text-center text-sm text-[#71717A]">
                Don't have an account?{' '}
                <button type="button" onClick={() => { setIsLogin(false); setLoginMode('phone'); resetPhoneLogin(); }} className="text-[#FF6A00] font-medium hover:underline">Sign Up</button>
              </p>
            </div>
          )}

          {/* ═══ EMAIL/PASSWORD LOGIN (fallback) + SIGNUP FORM ═══ */}
          {((isLogin && loginMode === 'email') || (!isLogin && signupStep === 1)) && (
            <form onSubmit={handleSubmit} className="space-y-4 mt-4 pb-4">
              <div>
                <Label htmlFor="username" className="text-[#0B0B0B] font-medium">
                  {isLogin ? 'Email or Username' : 'Username'}
                </Label>
                <Input
                  id="username"
                  name="username"
                  type="text"
                  data-testid="username-input"
                  value={formData.username}
                  onChange={handleInputChange}
                  placeholder={isLogin ? 'you@example.com or nick' : 'Choose a username'}
                  className="mt-1.5 h-12 border-[#E5E7EB] focus:border-[#FF6A00] focus:ring-[#FF6A00]/20"
                  required
                />
              </div>

              {!isLogin && (
                <>
                  <div>
                    <Label htmlFor="email" className="text-[#0B0B0B] font-medium">Email</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      data-testid="email-input"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="mt-1.5 h-12 border-[#E5E7EB] focus:border-[#FF6A00] focus:ring-[#FF6A00]/20"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="phone_number" className="text-[#0B0B0B] font-medium">
                      Phone Number
                      {phoneVerificationEnabled && <span className="text-orange-500 ml-1">*</span>}
                    </Label>
                    <div className="relative mt-1.5">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                      <Input
                        id="phone_number"
                        name="phone_number"
                        type="tel"
                        data-testid="phone-input"
                        value={formData.phone_number}
                        onChange={handlePhoneChange}
                        placeholder="(555) 123-4567"
                        className="h-12 pl-10 border-[#E5E7EB] focus:border-[#FF6A00] focus:ring-[#FF6A00]/20"
                        required={phoneVerificationEnabled}
                      />
                    </div>
                    {phoneVerificationEnabled && (
                      <p className="text-xs text-zinc-500 mt-1">
                        We'll send a verification code to confirm your number
                      </p>
                    )}
                  </div>

                  {/* SMS Opt-in Checkbox - Twilio Toll-Free Compliance (Error 30513) */}
                  <div className="p-3 bg-zinc-50 rounded-lg border border-zinc-200" data-testid="sms-consent-section">
                    {/* Checkbox with primary consent text */}
                    <div className="flex items-start space-x-3">
                      <Checkbox
                        id="sms_opt_in"
                        checked={formData.sms_opt_in}
                        onCheckedChange={(checked) => setFormData({ ...formData, sms_opt_in: checked })}
                        className="mt-0.5 data-[state=checked]:bg-[#FF6A00] data-[state=checked]:border-[#FF6A00]"
                        data-testid="sms-opt-in-checkbox"
                      />
                      <Label 
                        htmlFor="sms_opt_in" 
                        className="text-xs text-zinc-700 leading-relaxed cursor-pointer font-normal"
                      >
                        I agree to receive SMS text messages from Open Runs USA at the phone number provided regarding account verification, game reminders, and game updates. Consent is not a condition of participation.
                      </Label>
                    </div>
                    
                    {/* Message frequency and rates disclosure */}
                    <p className="text-xs text-zinc-500 mt-2 ml-7">
                      Message frequency varies. Message &amp; data rates may apply. Reply STOP to opt out, HELP for help.
                    </p>
                    
                    {/* Legal links - required for Twilio compliance */}
                    <p className="text-xs text-zinc-500 mt-2 ml-7">
                      View our{' '}
                      <a 
                        href="https://openrunsusa.com/terms-and-conditions" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-[#FF6A00] hover:underline font-medium"
                        data-testid="terms-link"
                      >
                        Terms of Service
                      </a>
                      {' '}and{' '}
                      <a 
                        href="https://openrunsusa.com/privacy-policy" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-[#FF6A00] hover:underline font-medium"
                        data-testid="privacy-link"
                      >
                        Privacy Policy
                      </a>.
                    </p>
                  </div>

                  <DateOfBirthPicker
                    value={formData.date_of_birth}
                    onChange={(val) => setFormData(prev => ({ ...prev, date_of_birth: val }))}
                    onValidationChange={setDobValidation}
                    required
                    minAge={13}
                  />
                  <p className="text-xs text-zinc-500 mt-1.5">
                    You must be 13 or older to join. By signing up, you confirm your age is accurate.
                  </p>
                </>
              )}

              <div>
                <Label htmlFor="password" className="text-[#0B0B0B] font-medium">Password</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  data-testid="password-input"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="mt-1.5 h-12 border-[#E5E7EB] focus:border-[#FF6A00] focus:ring-[#FF6A00]/20"
                  required
                />
                {isLogin && (
                  <div className="text-right mt-2">
                    <button
                      type="button"
                      onClick={() => {
                        setShowAuth(false);
                        setShowForgotPassword(true);
                      }}
                      className="text-sm text-[#FF6A00] hover:underline font-medium"
                    >
                      Forgot Password?
                    </button>
                  </div>
                )}
              </div>

              <Button 
                type="submit" 
                className="w-full bg-[#FF6A00] hover:bg-[#FF8A33] text-white h-12 text-base font-semibold rounded-xl transition-all duration-200" 
                data-testid="auth-submit-btn"
                disabled={isSendingCode}
              >
                {isSendingCode ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending Code...</>
                ) : (
                  isLogin ? 'Log In' : (phoneVerificationEnabled && formData.phone_number ? 'Verify Phone' : 'Create Account')
                )}
              </Button>

              <p className="text-center text-sm text-[#6B7280]">
                {isLogin ? "Don't have an account? " : "Already have an account? "}
                <button
                  type="button"
                  onClick={() => { setIsLogin(!isLogin); setLoginMode('phone'); resetPhoneLogin(); }}
                  className="text-[#FF6A00] font-semibold hover:underline"
                  data-testid="toggle-auth-btn"
                >
                  {isLogin ? 'Sign Up' : 'Log In'}
                </button>
              </p>
              {isLogin && (
                <button
                  type="button"
                  onClick={() => setLoginMode('phone')}
                  className="w-full text-center text-sm text-zinc-500 hover:text-zinc-700 py-1 transition-colors"
                  data-testid="switch-to-phone-login"
                >
                  Use phone number instead
                </button>
              )}
            </form>
          )}
          
          {/* Step 2: Phone Verification */}
          {!isLogin && signupStep === 2 && (
            <div className="space-y-5 mt-4">
              <div className="text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Phone className="w-8 h-8 text-[#FF6A00]" />
                </div>
                <p className="text-sm text-zinc-600">
                  We sent a 6-digit code to<br />
                  <span className="font-semibold text-zinc-900">{formData.phone_number}</span>
                </p>
              </div>
              
              <div>
                <Label htmlFor="code" className="text-[#0B0B0B] font-medium">Verification Code</Label>
                <Input
                  id="code"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  data-testid="verification-code-input"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="000000"
                  className="mt-1.5 h-14 text-center text-2xl tracking-[0.5em] font-mono border-[#E5E7EB] focus:border-[#FF6A00] focus:ring-[#FF6A00]/20"
                  autoFocus
                />
              </div>
              
              <Button 
                onClick={verifyCode}
                className="w-full bg-[#FF6A00] hover:bg-[#FF8A33] text-white h-12 text-base font-semibold rounded-xl transition-all duration-200" 
                data-testid="verify-code-btn"
                disabled={isVerifying || verificationCode.length !== 6}
              >
                {isVerifying ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verifying...</>
                ) : (
                  'Verify & Create Account'
                )}
              </Button>
              
              <div className="flex items-center justify-between text-sm">
                <button
                  type="button"
                  onClick={() => setSignupStep(1)}
                  className="text-zinc-500 hover:text-zinc-700 flex items-center gap-1"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Change number
                </button>
                
                <button
                  type="button"
                  onClick={sendVerificationCode}
                  disabled={!canResend || isSendingCode}
                  className={`font-medium ${canResend ? 'text-[#FF6A00] hover:underline' : 'text-zinc-400'}`}
                >
                  {canResend ? 'Resend code' : `Resend in ${resendTimer}s`}
                </button>
              </div>
            </div>
          )}
          </>
        );
        return isMobile ? (
          <Sheet open={showAuth} onOpenChange={handleOpenChange}>
            <SheetContent
              side="bottom"
              className="bg-white border-0 shadow-2xl rounded-t-2xl px-4 pt-6 pb-safe max-h-[92dvh] overflow-y-auto overscroll-contain"
              onOpenAutoFocus={(e) => e.preventDefault()}
              data-testid="auth-sheet-mobile"
            >
              {authBody}
            </SheetContent>
          </Sheet>
        ) : (
          <Dialog open={showAuth} onOpenChange={handleOpenChange}>
            <DialogContent
              className="max-w-md bg-white border-0 shadow-2xl pb-safe sm:pb-6"
              onOpenAutoFocus={(e) => e.preventDefault()}
              data-testid="auth-dialog-desktop"
            >
              {authBody}
            </DialogContent>
          </Dialog>
        );
      })()}

      {/* FORGOT PASSWORD DIALOG */}
      <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
        <DialogContent className="max-w-md bg-white border-0 shadow-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-bold text-center text-[#0B0B0B]">
              Reset Password
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#6B7280] text-center mb-4">
            Enter your email and we'll send you a reset link
          </p>
          <form onSubmit={handleForgotPassword} className="space-y-5">
            <div>
              <Label htmlFor="forgotEmail" className="text-[#0B0B0B] font-medium">Email</Label>
              <Input
                id="forgotEmail"
                type="email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                className="mt-1.5 h-12 border-[#E5E7EB] focus:border-[#FF6A00] focus:ring-[#FF6A00]/20"
                required
              />
            </div>
            <Button 
              type="submit" 
              className="w-full bg-[#FF6A00] hover:bg-[#FF8A33] text-white h-12 text-base font-semibold rounded-xl"
            >
              Send Reset Link
            </Button>
            <p className="text-center text-sm text-[#6B7280]">
              <button
                type="button"
                onClick={() => {
                  setShowForgotPassword(false);
                  setShowAuth(true);
                  setIsLogin(true);
                }}
                className="text-[#FF6A00] font-semibold hover:underline"
              >
                Back to Login
              </button>
            </p>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Landing;
